/**
 * LearningCenterModal - Training resources and learning materials
 * 
 * Includes:
 * - Keyboard Shortcuts reference
 * - Mouse & Trackpad Tips
 * - Video Tutorials (placeholder)
 * - Quick Start Guides
 * - External Resources
 */

import React, { useMemo, useState, useEffect } from 'react';
import { HelpVideoTourModal } from './HelpVideoTour';
import { listHelpTours, PIPING_WALKTHROUGH_GUIDE_ID } from './helpTours/registry';

const LearningCenterModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('shortcuts');
  const [activeTour, setActiveTour] = useState(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: '⌨️' },
    { id: 'mouse', label: 'Mouse & Trackpad', icon: '🖱️' },
    { id: 'videos', label: 'Video Tutorials', icon: '🎬' },
    { id: 'guides', label: 'Quick Guides', icon: '📋' },
    { id: 'resources', label: 'Resources', icon: '📚' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          maxWidth: '1000px',
          height: '80vh',
          maxHeight: '700px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s ease'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 28px',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #dc3545 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>
              🎓 Learning Center
            </h1>
            <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
              Master LGL Portal with tutorials and guides
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'white',
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          padding: '0 16px',
          overflowX: 'auto'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '14px 20px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #dc3545' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: activeTab === tab.id ? '600' : '500',
                color: activeTab === tab.id ? '#dc3545' : '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
          {activeTab === 'shortcuts' && <KeyboardShortcutsTab />}
          {activeTab === 'mouse' && <MouseGesturesTab />}
          {activeTab === 'videos' && <VideoTutorialsTab onOpenTour={setActiveTour} />}
          {activeTab === 'guides' && <QuickGuidesTab />}
          {activeTab === 'resources' && <ResourcesTab />}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 28px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#374151',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#374151'}
          >
            Close
          </button>
        </div>
      </div>

      <HelpVideoTourModal
        isOpen={!!activeTour}
        onClose={() => setActiveTour(null)}
        guideId={activeTour?.guideId}
        title={activeTour?.title}
        accent={activeTour?.accent}
        scenes={activeTour?.scenes}
      />

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { 
            opacity: 0; 
            transform: translate(-50%, -48%);
          }
          to { 
            opacity: 1; 
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </>
  );
};

// ============================================
// KEYBOARD SHORTCUTS TAB
// ============================================
const KeyboardShortcutsTab = () => {
  const shortcutGroups = [
    {
      title: 'Navigation',
      shortcuts: [
        { keys: ['Esc'], description: 'Close modal / Cancel editing' },
        { keys: ['Ctrl/Cmd', 'F'], description: 'Open browser search' },
        { keys: ['Ctrl/Cmd', 'P'], description: 'Print current page' },
      ]
    },
    {
      title: 'Table Editing',
      shortcuts: [
        { keys: ['Tab'], description: 'Move to next cell' },
        { keys: ['Shift', 'Tab'], description: 'Move to previous cell' },
        { keys: ['Enter'], description: 'Save and move down' },
        { keys: ['Escape'], description: 'Cancel current edit' },
        { keys: ['Arrow Keys'], description: 'Navigate between cells' },
      ]
    },
    {
      title: 'Selection',
      shortcuts: [
        { keys: ['Click', 'Drag'], description: 'Select range of cells' },
        { keys: ['Shift', 'Click'], description: 'Extend selection to clicked cell' },
        { keys: ['Ctrl/Cmd', 'Click'], description: 'Add cell to selection' },
        { keys: ['Ctrl/Cmd', 'A'], description: 'Select all visible rows' },
      ]
    },
    {
      title: 'Quick Actions',
      shortcuts: [
        { keys: ['Delete'], description: 'Clear selected cells' },
        { keys: ['Ctrl/Cmd', 'C'], description: 'Copy selected cells' },
        { keys: ['Ctrl/Cmd', 'V'], description: 'Paste into cells' },
        { keys: ['Ctrl/Cmd', 'Z'], description: 'Undo last action' },
      ]
    }
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: '#111827', fontSize: '1.25rem' }}>
        ⌨️ Keyboard Shortcuts Reference
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Use these keyboard shortcuts to work faster in LGL Portal.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        {shortcutGroups.map((group) => (
          <div
            key={group.title}
            style={{
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb'
            }}
          >
            <h3 style={{ margin: '0 0 16px', color: '#374151', fontSize: '1rem', fontWeight: '600' }}>
              {group.title}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {group.shortcuts.map((shortcut, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < group.shortcuts.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {shortcut.keys.map((key, i) => (
                      <span key={i}>
                        <kbd style={{
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          padding: '3px 8px',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}>
                          {key}
                        </kbd>
                        {i < shortcut.keys.length - 1 && (
                          <span style={{ margin: '0 4px', color: '#9ca3af' }}>+</span>
                        )}
                      </span>
                    ))}
                  </div>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem', textAlign: 'right' }}>
                    {shortcut.description}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// MOUSE GESTURES TAB
// ============================================
const MouseGesturesTab = () => {
  const gestures = [
    {
      title: 'Drag-Fill (Excel-like)',
      icon: '↘️',
      description: 'Fill values into adjacent cells',
      steps: [
        'Select a cell with a value',
        'Grab the small square handle at the bottom-right corner',
        'Drag down or across to fill the value',
        'Release to apply'
      ]
    },
    {
      title: 'Multi-Cell Selection',
      icon: '⬚',
      description: 'Select multiple cells at once',
      steps: [
        'Click and hold on the starting cell',
        'Drag to the ending cell',
        'Release to complete selection',
        'Selected cells will be highlighted'
      ]
    },
    {
      title: 'Column Resize',
      icon: '↔️',
      description: 'Adjust column widths',
      steps: [
        'Hover over the column border in the header',
        'Cursor changes to resize indicator',
        'Click and drag to adjust width',
        'Width is saved automatically'
      ]
    },
    {
      title: 'Sidebar Resize',
      icon: '◫',
      description: 'Adjust sidebar width',
      steps: [
        'Hover over the right edge of the sidebar',
        'Cursor changes to resize indicator',
        'Drag left or right to adjust',
        'Your preference is saved'
      ]
    },
    {
      title: 'Right-Click Context Menu',
      icon: '📋',
      description: 'Access quick actions',
      steps: [
        'Right-click on any cell or row',
        'Context menu appears with options',
        'Select an action (copy, delete, etc.)',
        'Action is applied immediately'
      ]
    },
    {
      title: 'Double-Click to Edit',
      icon: '✏️',
      description: 'Quick edit mode',
      steps: [
        'Click any editable cell',
        'Cell enters edit mode',
        'Type your value',
        'Click outside or press Enter to save'
      ]
    }
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: '#111827', fontSize: '1.25rem' }}>
        🖱️ Mouse & Trackpad Tips
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Master these gestures to work efficiently with tables and data.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        {gestures.map((gesture) => (
          <div
            key={gesture.title}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{
                fontSize: '1.5rem',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fef2f2',
                borderRadius: '10px'
              }}>
                {gesture.icon}
              </span>
              <div>
                <h3 style={{ margin: 0, color: '#111827', fontSize: '0.95rem', fontWeight: '600' }}>
                  {gesture.title}
                </h3>
                <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
                  {gesture.description}
                </p>
              </div>
            </div>
            <ol style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '0.85rem', lineHeight: '1.7' }}>
              {gesture.steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// VIDEO TUTORIALS TAB
// ============================================
const VideoTutorialsTab = ({ onOpenTour }) => {
  const tours = useMemo(() => listHelpTours(), []);
  const featured = tours.find((t) => t.guideId === PIPING_WALKTHROUGH_GUIDE_ID) || tours[0];
  const byGroup = useMemo(() => {
    const map = new Map();
    for (const t of tours) {
      if (featured && t.guideId === featured.guideId) continue;
      const g = t.group || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(t);
    }
    return [...map.entries()];
  }, [tours, featured]);

  return (
    <div>
      <h2 style={{ margin: '0 0 8px', color: '#111827', fontSize: '1.25rem' }}>
        🎬 Video Tours
      </h2>
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
        Illustrated walkthroughs with Clara + Liam narration — {tours.length} tours available from help guides across the portal.
      </p>

      {featured && (
        <button
          type="button"
          onClick={() => onOpenTour(featured)}
          style={{
            width: '100%',
            textAlign: 'left',
            border: '1px solid #bae6fd',
            background: `linear-gradient(135deg, #0c4a6e 0%, ${featured.accent || '#0369a1'} 55%, #7dd3fc 100%)`,
            borderRadius: 14,
            padding: 0,
            cursor: 'pointer',
            marginBottom: 24,
            overflow: 'hidden',
            boxShadow: '0 10px 28px rgba(3,105,161,0.28)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', minHeight: 140 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.18)', color: 'white', fontSize: 42,
            }}>
              ▶
            </div>
            <div style={{ padding: '20px 22px', color: 'white' }}>
              <div style={{
                display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 1,
                background: 'rgba(255,255,255,0.2)', borderRadius: 999, padding: '3px 10px', marginBottom: 8,
              }}>
                FEATURED · {featured.group || 'Tour'}
              </div>
              <h3 style={{ margin: '0 0 6px', fontSize: '1.2rem', fontWeight: 800 }}>
                {featured.title}
              </h3>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.92, lineHeight: 1.45, maxWidth: 520 }}>
                Graphical walkthrough synced to Clara + Liam audio narration.
              </p>
              <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700, opacity: 0.9 }}>
                {featured.scenes?.length || 0} chapters · click to play
              </div>
            </div>
          </div>
        </button>
      )}

      {byGroup.map(([group, items]) => (
        <div key={group} style={{ marginBottom: 22 }}>
          <h3 style={{ margin: '0 0 12px', color: '#374151', fontSize: '1rem' }}>{group}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {items.map((tour) => (
              <button
                key={tour.guideId}
                type="button"
                onClick={() => onOpenTour(tour)}
                style={{
                  textAlign: 'left',
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  borderRadius: 10,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: tour.accent || '#0369a1', marginBottom: 4 }}>
                  ▶ VIDEO TOUR
                </div>
                <div style={{ fontWeight: 700, color: '#111827', fontSize: 14, marginBottom: 4 }}>
                  {tour.title}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {tour.scenes?.length || 0} chapters
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// QUICK GUIDES TAB
// ============================================
const QuickGuidesTab = () => {
  const guides = [
    {
      title: 'Upload Progress Data',
      time: '5 min',
      icon: '📤',
      steps: [
        'Navigate to Progress Tracking > Piping (or other discipline)',
        'Click the "Import" button in the toolbar',
        'Drag and drop your Excel file or click to browse',
        'Review the column mapping preview',
        'Click "Upload" to process the data',
        'Wait for confirmation message'
      ]
    },
    {
      title: 'Export Data to Excel',
      time: '3 min',
      icon: '📥',
      steps: [
        'Open any tracking dashboard or log',
        'Apply filters if you want specific data',
        'Click the "Export" button',
        'Choose export type: Dashboard Only, All Columns, or Full',
        'Download starts automatically'
      ]
    },
    {
      title: 'Edit Data Inline',
      time: '2 min',
      icon: '✏️',
      steps: [
        'Click any editable cell to start editing',
        'Type your new value',
        'Press Tab to move to next cell, Enter to save and move down',
        'Press Escape to cancel changes',
        'Changes are auto-saved when you leave the cell'
      ]
    },
    {
      title: 'Configure Columns',
      time: '4 min',
      icon: '⚙️',
      steps: [
        'Click the Column Config button (⚙️) in toolbar',
        'Check/uncheck columns to show or hide',
        'Use "Add UDF" to create custom columns',
        'Drag columns to reorder (if supported)',
        'Click Save - preferences are stored per project'
      ]
    },
    {
      title: 'View Progress Trends',
      time: '2 min',
      icon: '📈',
      steps: [
        'Go to Progress Tracking > Piping',
        'Click any line tag (blue link in first column)',
        'A modal shows historical progress chart',
        'View fabrication, painting, installation trends',
        'Close modal to return to the tracker'
      ]
    },
    {
      title: 'Submit a Support Ticket',
      time: '3 min',
      icon: '🎫',
      steps: [
        'Click "?" in header > Contact Us & Support',
        'Click "New Ticket" button',
        'Select an issue category',
        'Describe your issue in detail',
        'Click Submit - we\'ll respond soon'
      ]
    }
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: '#111827', fontSize: '1.25rem' }}>
        📋 Quick Start Guides
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Step-by-step guides for common tasks. Each takes just a few minutes!
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
        {guides.map((guide) => (
          <div
            key={guide.title}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
          >
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.3rem' }}>{guide.icon}</span>
                <h3 style={{ margin: 0, color: '#111827', fontSize: '0.95rem', fontWeight: '600' }}>
                  {guide.title}
                </h3>
              </div>
              <span style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                {guide.time}
              </span>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <ol style={{ margin: 0, paddingLeft: '18px', color: '#374151', fontSize: '0.85rem', lineHeight: '1.8' }}>
                {guide.steps.map((step, idx) => (
                  <li key={idx} style={{ marginBottom: '4px' }}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// RESOURCES TAB
// ============================================
const ResourcesTab = () => {
  const resources = [
    {
      title: 'LGL Portal Documentation',
      description: 'Complete technical documentation and API reference',
      icon: '📚',
      link: null,
      type: 'internal'
    },
    {
      title: 'Team README',
      description: 'Development team documentation and change log',
      icon: '📝',
      link: null,
      type: 'internal'
    },
    {
      title: 'Migration Notes',
      description: 'Database migration history and schema changes',
      icon: '🗄️',
      link: null,
      type: 'internal'
    }
  ];

  const tips = [
    'Use the Project Dropdown in header for quick project switching',
    'Press Ctrl+F (Cmd+F on Mac) to search any page',
    'Export data regularly to keep backups',
    'Configure visible columns to see only what you need',
    'The system auto-saves your work - no save button needed',
    'Collapse the sidebar for more workspace on the screen'
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: '#111827', fontSize: '1.25rem' }}>
        📚 Resources & Documentation
      </h2>

      {/* Quick Tips */}
      <div style={{
        backgroundColor: '#ecfdf5',
        border: '1px solid #a7f3d0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 12px', color: '#065f46', fontSize: '1rem' }}>
          💡 Quick Tips
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#047857', fontSize: '0.9rem', lineHeight: '1.8' }}>
          {tips.map((tip, idx) => (
            <li key={idx}>{tip}</li>
          ))}
        </ul>
      </div>

      {/* Resources Grid */}
      <h3 style={{ margin: '0 0 16px', color: '#374151', fontSize: '1rem' }}>
        Documentation
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {resources.map((resource) => (
          <div
            key={resource.title}
            style={{
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '1.5rem' }}>{resource.icon}</span>
              <h4 style={{ margin: 0, color: '#111827', fontSize: '0.95rem' }}>{resource.title}</h4>
            </div>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }}>
              {resource.description}
            </p>
          </div>
        ))}
      </div>

      {/* System Info */}
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        padding: '20px',
        color: 'white'
      }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '1rem' }}>System Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginBottom: '4px' }}>Version</div>
            <div style={{ fontWeight: '600' }}>LGL Portal v2.0</div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginBottom: '4px' }}>Last Updated</div>
            <div style={{ fontWeight: '600' }}>December 2025</div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginBottom: '4px' }}>Browser Support</div>
            <div style={{ fontWeight: '600' }}>Chrome, Edge, Firefox, Safari</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningCenterModal;
